<template>
  <div
    v-if="authFlowState.type === 'loading'"
    class="flex flex-col items-center"
  >
    <HoppSmartSpinner />
  </div>
  <div
    v-else-if="authFlowState.type === 'waiting'"
    class="flex flex-col items-center space-y-2"
  >
    <h2 class="text-secondaryDark font-semibold text-lg">
      Continue in your browser
    </h2>
    <p>We have opened Hoppscotch in your browser to continue your login.</p>
    <div class="py-3 flex space-x-4 items-center">
      <HoppButtonPrimary
        :label="'Open Again'"
        :icon="IconRotateCW"
        @click="openLink(authFlowState.openURL)"
      />
      <HoppButtonSecondary
        :label="'Copy Link'"
        :icon="copyIcon"
        outline
        @click="onCopyClick"
      />
    </div>
  </div>
  <div v-else class="flex flex-col items-center">
    <p>There was an error processing your login...</p>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue"
import { refAutoReset } from "@vueuse/core"
import { invoke } from "@tauri-apps/api/core"
import { listen } from "@tauri-apps/api/event"
import { z } from "zod"
import { getAuth, signInWithCustomToken } from "firebase/auth"
import IconRotateCW from "~icons/lucide/rotate-cw"
import IconLink from "~icons/lucide/link"
import IconCheck from "~icons/lucide/check"
import { Io } from "@hoppscotch/common/kernel/io"
import { copyToClipboard } from "@helpers/utils/clipboard"
import { getOrganizationDomain } from "@app/helpers/utils/cloud-for-orgs"
import { buildDeviceLoginURL } from "@app/helpers/auth/device-login"

const DeviceTokenResponse = z.object({
  access_token: z.string(),
  refresh_token: z.string().nullable(),
})

type FlowStates =
  | { type: "loading" }
  | { type: "waiting"; openURL: string }
  | { type: "error" }

const authFlowState = ref<FlowStates>({ type: "loading" })
const copyIcon = refAutoReset(IconLink, 1000)

function onCopyClick() {
  if (authFlowState.value.type === "waiting") {
    copyIcon.value = IconCheck
    copyToClipboard(authFlowState.value.openURL)
  }
}

async function openLink(url: string) {
  await Io.openExternalLink({ url })
}

onMounted(async () => {
  const port = await invoke<number>("hopp_auth_port")

  const redirectURI = `http://127.0.0.1:${port}/device-token`

  // the page stays on default cloud, where the device flow already runs,
  // and gets the org window's org as a param.
  const openURL = buildDeviceLoginURL(
    import.meta.env.VITE_BASE_URL,
    redirectURI,
    getOrganizationDomain()
  )

  // only open the browser login if this webview has org context or is the
  // only webview (vendored-only mode). when the user is logging in from
  // an org webview, the vendored webview's Login component also mounts but
  // should not open a duplicate browser tab.
  const hasOrgContext = !!new URLSearchParams(window.location.search).get("org")
  if (hasOrgContext) {
    await openLink(openURL)
  } else {
    // vendored: only open the browser if no other webview windows exist
    // (i.e. this is a standalone vendored login, not a background webview
    // while an org webview is in the foreground). we detect this by
    // checking if there are other Tauri windows besides the current one.
    try {
      const { getAllWebviewWindows, getCurrentWebviewWindow } =
        await import("@tauri-apps/api/webviewWindow")
      const allWindows = await getAllWebviewWindows()
      const currentLabel = getCurrentWebviewWindow().label
      const otherWindows = allWindows.filter(
        (w: { label: string }) => w.label !== currentLabel && w.label !== "main"
      )
      if (otherWindows.length === 0) {
        // no other webviews, this is a standalone vendored login
        await openLink(openURL)
      }
      // else: another webview exists (likely org), skip the browser open
    } catch (_) {
      // fallback: open the link if we can't check windows
      await openLink(openURL)
    }
  }
  authFlowState.value = { type: "waiting", openURL }

  // the `hopp_auth://token` event is a global Tauri emit (handle.emit),
  // meaning ALL webviews receive it. this is a problem when both the
  // vendored and org webview are open: both will call signInWithCustomToken
  // and both will reload. to prevent the vendored webview from clobbering
  // the org webview's state, only the webview that initiated the login
  // (authFlowState === "waiting") should process the token and reload.
  await listen<string>("hopp_auth://token", async (data) => {
    const orgParam = new URLSearchParams(window.location.search).get("org")
    const tag = orgParam ? `org(${orgParam})` : "vendored"

    // write to disk so we can see this even if webkit wipes the console
    if ((window as any).__TAURI_INTERNALS__) {
      ;(window as any).__TAURI_INTERNALS__
        .invoke("append_log", {
          filename: "io.hoppscotch.desktop.diag.log",
          content: `[${new Date().toISOString()}] [AUTH-TOKEN] [${tag}] hopp_auth://token received, href=${window.location.href}, flowState=${authFlowState.value.type}\n`,
        })
        .catch(() => {})
    }

    // when an org webview is active, the vendored webview also receives this
    // global event. if the vendored webview reloads, its detectCurrentInstance
    // will overwrite the org connection state with vendored in the unified
    // store. to prevent this, the vendored webview signs in silently (so
    // firebase auth is ready) but skips the reload.
    const isVendored = !orgParam
    if (isVendored) {
      if ((window as any).__TAURI_INTERNALS__) {
        ;(window as any).__TAURI_INTERNALS__
          .invoke("append_log", {
            filename: "io.hoppscotch.desktop.diag.log",
            content: `[${new Date().toISOString()}] [AUTH-TOKEN] [${tag}] vendored webview: signing in silently without reload\n`,
          })
          .catch(() => {})
      }

      try {
        const parseResult = DeviceTokenResponse.safeParse(data.payload)
        if (parseResult.success) {
          const fbAuth = getAuth()
          await signInWithCustomToken(fbAuth, parseResult.data.access_token)
        }
      } catch (_) {
        // silent sign-in failure is non-fatal, swallow and move on
      }
      return
    }

    authFlowState.value = { type: "loading" }
    try {
      const parseResult = DeviceTokenResponse.safeParse(data.payload)

      if (!parseResult.success) {
        throw new Error("Token data returned from backend was invalid")
      }

      const fbAuth = getAuth()
      await signInWithCustomToken(fbAuth, parseResult.data.access_token)

      if ((window as any).__TAURI_INTERNALS__) {
        ;(window as any).__TAURI_INTERNALS__
          .invoke("append_log", {
            filename: "io.hoppscotch.desktop.diag.log",
            content: `[${new Date().toISOString()}] [AUTH-TOKEN] [${tag}] signInWithCustomToken succeeded, about to reload\n`,
          })
          .catch(() => {})
      }

      // bring this webview window to the foreground before reloading.
      // after the system browser login flow, the vendored window may be
      // on top (it was the last-focused Hoppscotch window before the
      // browser opened). without this, the user switches back to the app
      // and sees vendored instead of the org webview they just logged into.
      try {
        const { getCurrentWebviewWindow } =
          await import("@tauri-apps/api/webviewWindow")
        await getCurrentWebviewWindow().setFocus()
      } catch (_) {
        // setFocus can fail on some platforms, don't block auth on it
      }

      window.location.reload()
    } catch (_) {
      authFlowState.value = { type: "error" }
    }
  })
})
</script>
